import { MessageCircle } from "lucide-react";
import { useAccountId } from "../auth/useAccountId";
import "../announcement-banner.css";
import type { AnnouncementConfig } from "@shared/domain/announcementConfig";
import { TARGETED_ANNOUNCEMENT_WALLETS } from "@shared/domain/targetedAnnouncementWallets";
import { useT } from "../i18n/i18nContext";

interface AnnouncementBannerProps extends AnnouncementConfig {
  /**
   * Show the announcement banner
   */
  enabled: boolean;
}

/**
 * Full-width announcement banner for displaying site-wide messages
 *
 * This component is NOT dismissible - it always stays at the bottom if enabled.
 * Automatically shows different messages based on wallet connection status:
 * - notConnectedMessage: When no wallet is connected or not in targeted list
 * - connectedMessage: When a wallet is connected AND in the targeted wallets list
 *
 * Control visibility and content via shared/domain/announcementConfig.ts
 * Control which wallets see connectedMessage via shared/domain/targetedAnnouncementWallets.ts
 *
 * Examples: maintenance notifications, season updates, emergency announcements
 *
 * Usage (via config):
 * 1. Edit shared/domain/announcementConfig.ts
 * 2. Set enabled: true
 * 3. Update notConnectedMessage and connectedMessage
 * 4. Choose type (info | success | warning | critical)
 * 5. Add wallet addresses to targetedAnnouncementWallets.ts to show connectedMessage
 */
export function AnnouncementBanner({
  enabled,
  notConnectedMessage,
  connectedMessage,
  type = "info",
}: AnnouncementBannerProps) {
  const t = useT();
  const { address: accountAddress } = useAccountId();

  // Determine which message to show based on login state and targeted wallets
  const isWalletTargeted =
    accountAddress &&
    (TARGETED_ANNOUNCEMENT_WALLETS.includes("*") ||
      TARGETED_ANNOUNCEMENT_WALLETS.includes("all") ||
      TARGETED_ANNOUNCEMENT_WALLETS.some(
        (walletAddress) =>
          walletAddress.toLowerCase() === accountAddress.toLowerCase()
      ));

  const message = isWalletTargeted ? connectedMessage : notConnectedMessage;

  if (!enabled || !message) {
    return null;
  }

  return (
    <div
      className={`announcement-banner announcement-banner--${type}`}
      role="status"
      aria-live="polite"
      aria-label={t("navigation.app.announcementAria", { message })}
    >
      <div className="announcement-banner__inner">
        <p className="announcement-banner__message">{message}</p>
        <a
          href="https://x.com/suikamongame"
          target="_blank"
          rel="noopener noreferrer"
          className="announcement-banner__feedback"
          aria-label={t("navigation.app.feedbackAria")}
        >
          <MessageCircle size={20} />
          <span>{t("navigation.app.contact")}</span>
        </a>
      </div>
    </div>
  );
}
